'use client';

import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { Token, token } from '@flipster/styled-system/tokens';

import {
  ALLOCATION_CHART_COLORS,
  ALLOCATION_CHART_OTHERS_COLOR,
  AllocationChart as AllocationChartBase,
  type AllocationChartItem,
} from '../../../../../shared/allocation-chart';
import { useProductConfig } from '../../product-config';

export const PortfolioBreakdownChart = () => {
  return (
    <div
      className={stack({
        bg: 'background.default.level-1',
        rounded: 12,
        gap: 16,
        tabletDown: { p: 16 },
        tablet: { p: 20 },
      })}
    >
      <div
        className={flex({
          tabletDown: { flexDirection: 'column', gap: 16 },
          tablet: { flexDirection: 'row', flexWrap: 'wrap', gap: 64 },
        })}
      >
        <div className={stack({ tabletDown: { gap: 16 }, tablet: { gap: 32 } })}>
          <AllocationHeader />
          <AllocationChart />
        </div>
        <div className={stack({ flex: 1 })}>
          <AllocationTable />
          <AssetExposure />
        </div>
      </div>
      <LatestUpdate />
    </div>
  );
};

const AllocationHeader = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  return (
    <div className={stack({ gap: 4 })}>
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
        <FormattedMessage id='Earn.r251208.Portfolio_breakdown' />
      </p>
      <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
        <Formatted.ShortNumber value={details?.portfolioBreakdown?.total} suffix='USDT' />
      </p>
    </div>
  );
};

const AllocationChart = () => {
  const { sortedStrategiesAtom } = useProductConfig();
  const strategies = useAtomValue(sortedStrategiesAtom);

  const items: AllocationChartItem[] = useMemo(
    () =>
      strategies.map((strategy) => ({
        key: strategy.name,
        percentage: strategy.allocationPercentage,
        tooltipContent: (
          <>
            {strategy.name}
            {'  '}
            <Formatted.Percent value={strategy.allocationPercentage} precision={2} toFixed isRate />
          </>
        ),
      })),
    [strategies],
  );

  return <AllocationChartBase items={items} emptyMessage='No asset data to show' />;
};

const AllocationTable = () => {
  const { sortedStrategiesAtom } = useProductConfig();
  const strategies = useAtomValue(sortedStrategiesAtom);

  return (
    <div>
      <div
        className={hstack({
          textStyle: 'Text/XS/Regular',
          justify: 'space-between',
          color: 'content.default.level-2',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: 'border.default.level-1',
          pb: 8,
          mb: 8,
        })}
      >
        <p>
          <FormattedMessage id='Earn.r251208.Strategy' />
        </p>
        <p className={css({ textAlign: 'right' })}>
          <FormattedMessage id='Earn.r251208.Allocation_token' values={{ token: 'USDT' }} />
        </p>
      </div>
      <ul>
        {strategies.map((strategy, idx) => {
          const color =
            idx < ALLOCATION_CHART_COLORS.length ? ALLOCATION_CHART_COLORS[idx] : ALLOCATION_CHART_OTHERS_COLOR;
          return (
            <li key={`${strategy.name}-${idx}`} className={hstack({ justify: 'space-between', py: 8 })}>
              <div className={hstack({ gap: 12 })}>
                <ColorSquare color={color} />
                <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
                  {strategy.name}
                </span>
              </div>
              <div className={stack({ align: 'flex-end', gap: 4 })}>
                <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                  <Formatted.Percent value={strategy.allocationPercentage} precision={2} toFixed isRate />
                </span>
                <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
                  <Formatted.ShortNumber value={strategy.allocationAmount} toFixed />
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ColorSquare = ({ color }: { color: Token }) => {
  return <div className={css({ w: 12, h: 12, rounded: 2, flexShrink: 0 })} style={{ backgroundColor: token(color) }} />;
};

const AssetExposure = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  return (
    <div
      className={stack({
        gap: 4,
        py: 16,
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderColor: 'border.default.level-1',
      })}
    >
      <Tooltip.Root placement='right' maxWidth={280}>
        <Tooltip.TextTrigger
          label={<FormattedMessage id='Earn.r251208.Asset_exposure' />}
          css={{ textStyle: 'Text/S/Regular', color: 'content.default.level-2' }}
        />
        <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r251208.Asset_exposure' />}>
          <FormattedMessage id='Earn.r251208.Asset_exposure_overview_desc' />
        </Tooltip.Content>
      </Tooltip.Root>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
        {details?.assetExposure ?? '-'}
      </p>
    </div>
  );
};

const LatestUpdate = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  return (
    <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', textAlign: 'right' })}>
      <FormattedMessage
        id='Common.r251107.Latest_update_colon_date_time'
        values={{
          dateTime: (
            <Datetime
              key='dateTime'
              value={details?.portfolioBreakdown?.latestUpdate}
              format='yyyy.MM.dd, HH:mm'
              isUTC
              withUTCText
            />
          ),
        }}
      />
    </p>
  );
};
